import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, TRANSITIONS, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { BackupSection } from "@/components/BackupSection";
import { CategoriesSection } from "@/components/CategoriesSection";
import { PasswordDialog } from "@/components/PasswordDialog";
import { EmailDialog } from "@/components/EmailDialog";
import { PosterMailSection } from "@/components/PosterMailSection";
import { ScreensSection } from "@/components/ScreensSection";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Loader2, UserPlus, Trash2, ShieldCheck, Shield, Sparkles, Save, PlusCircle, MinusCircle, History, Tv, Mail, Users, DatabaseBackup } from "lucide-react";

const TABS = [
  { value: "tv", label: "Diffusion TV", Icon: Tv },
  { value: "mail", label: "Affiches & mail", Icon: Mail },
  { value: "accounts", label: "Comptes", Icon: Users },
  { value: "backup", label: "Sauvegardes & journal", Icon: DatabaseBackup },
];

export default function Administration() {
  useEffect(() => {
    document.documentElement.classList.add("theme-light");
    return () => document.documentElement.classList.remove("theme-light");
  }, []);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [admins, setAdmins] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("secondaire");
  const [creating, setCreating] = useState(false);
  const [transition, setTransition] = useState("fade");
  const [savingTx, setSavingTx] = useState(false);
  const [activity, setActivity] = useState(null);

  const load = async () => {
    const [a, s, act] = await Promise.all([api.get("/admins"), api.get("/settings"), api.get("/activity").catch(() => ({ data: [] }))]);
    setAdmins(a.data);
    setTransition(s.data.default_transition || "fade");
    setActivity(act.data);
  };
  useEffect(() => {
    load();
  }, []);

  const createAdmin = async () => {
    if (!email.trim() || !password.trim()) {
      toast.error("Email et mot de passe requis");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/admins", { email: email.trim(), password, name: name.trim() || "Administrateur", role });
      setAdmins((list) => [...(list || []), data]);
      setEmail(""); setName(""); setPassword(""); setRole("secondaire");
      toast.success("Administrateur créé");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec de la création");
    } finally {
      setCreating(false);
    }
  };

  const removeAdmin = async (id) => {
    try {
      await api.delete(`/admins/${id}`);
      setAdmins((list) => list.filter((a) => a.id !== id));
      toast.success("Administrateur supprimé");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec de la suppression");
    }
  };

  const saveTransition = async () => {
    setSavingTx(true);
    try {
      await api.put("/settings", { default_transition: transition });
      toast.success("Transition par défaut enregistrée");
    } catch {
      toast.error("Échec de l'enregistrement");
    } finally {
      setSavingTx(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="admin-page-light">
      <div className="grain-overlay pointer-events-none fixed inset-0 opacity-60" />
      <header className="sticky top-11 z-30 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-4 sm:px-6 lg:px-8">
          <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-button">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-display text-xl font-extrabold tracking-tight">Administration</h1>
            <p className="font-mono-label text-[10px] uppercase tracking-widest text-muted-foreground">
              Gestion des accès &amp; réglages
            </p>
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
      <Tabs defaultValue="tv" className="space-y-6">
        <TabsList className="grid h-auto w-full grid-cols-2 gap-1 sm:grid-cols-4" data-testid="admin-tabs">
          {TABS.map(({ value, label, Icon }) => (
            <TabsTrigger key={value} value={value} className="gap-1.5 px-1 py-2 text-xs sm:gap-2 sm:text-sm" data-testid={`admin-tab-${value}`}>
              <Icon className="h-4 w-4" /> {label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="tv" className="space-y-8" data-testid="admin-panel-tv">
        <ScreensSection />
        <CategoriesSection />
        <section className="rounded-2xl border border-border bg-card p-6">
          <div className="mb-4 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-accent" />
            <h2 className="font-display text-lg font-semibold">Transition par défaut</h2>
          </div>
          <p className="mb-4 text-sm text-muted-foreground">
            Applique cette transition automatiquement à toutes les nouvelles images ajoutées aux bandeaux.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Transition</Label>
              <Select value={transition} onValueChange={setTransition}>
                <SelectTrigger className="w-56" data-testid="default-transition-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TRANSITIONS.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={saveTransition} disabled={savingTx} data-testid="save-settings-button">
              {savingTx ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
              Enregistrer
            </Button>
          </div>
        </section>

        </TabsContent>
        <TabsContent value="mail" className="space-y-8" data-testid="admin-panel-mail">
          <PosterMailSection />
        </TabsContent>
        <TabsContent value="accounts" className="space-y-8" data-testid="admin-panel-accounts">
        {/* create admin */}
        <section className="rounded-2xl border border-border bg-card p-6">
          <div className="mb-4 flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" />
            <h2 className="font-display text-lg font-semibold">Créer un administrateur</h2>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@club.com" data-testid="admin-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Nom</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom" data-testid="admin-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Mot de passe</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••" data-testid="admin-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Rôle</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="admin-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="principal">Principal (tout modifier)</SelectItem>
                  <SelectItem value="secondaire">Secondaire (images uniquement)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4">
            <Button onClick={createAdmin} disabled={creating} data-testid="create-admin-button">
              {creating ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <UserPlus className="mr-1 h-4 w-4" />}
              Créer l'administrateur
            </Button>
          </div>
        </section>

        {/* admins list */}
        <section className="rounded-2xl border border-border bg-card p-6">
          <h2 className="mb-4 font-display text-lg font-semibold">Administrateurs</h2>
          {admins === null ? (
            <div className="flex justify-center py-8 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : (
            <div className="space-y-2">
              {admins.map((a, i) => (
                <motion.div
                  key={a.id}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                  className="flex items-center gap-3 rounded-xl border border-border p-3"
                  data-testid={`admin-row-${a.id}`}
                >
                  {a.role === "principal" ? <ShieldCheck className="h-5 w-5 text-accent" /> : <Shield className="h-5 w-5 text-muted-foreground" />}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{a.name} {a.id === user?.id && <span className="text-muted-foreground">(vous)</span>}</p>
                    <p className="truncate text-xs text-muted-foreground">{a.email}</p>
                  </div>
                  <Badge variant={a.role === "principal" ? "default" : "secondary"}>
                    {a.role === "principal" ? "Principal" : "Secondaire"}
                  </Badge>
                  <EmailDialog admin={a} onSaved={(id, email) => setAdmins((list) => list.map((x) => (x.id === id ? { ...x, email } : x)))} />
                  <PasswordDialog admin={a} />
                  {a.id !== user?.id && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="icon" variant="ghost" className="text-destructive hover:text-destructive" data-testid={`delete-admin-${a.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Supprimer cet administrateur ?</AlertDialogTitle>
                          <AlertDialogDescription>« {a.email} » perdra l'accès à la plateforme.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annuler</AlertDialogCancel>
                          <AlertDialogAction onClick={() => removeAdmin(a.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid={`confirm-delete-admin-${a.id}`}>
                            Supprimer
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </section>

        </TabsContent>
        <TabsContent value="backup" className="space-y-8" data-testid="admin-panel-backup">
        <BackupSection />
        {/* activity journal */}
        <section className="rounded-2xl border border-border bg-card p-6">
          <div className="mb-4 flex items-center gap-2">
            <History className="h-5 w-5 text-accent" />
            <h2 className="font-display text-lg font-semibold">Journal des modifications</h2>
          </div>
          <p className="mb-4 text-sm text-muted-foreground">
            Ajouts et suppressions d'images effectués dans les bandeaux, par administrateur.
          </p>
          {activity === null ? (
            <div className="flex justify-center py-8 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : activity.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">Aucune activité enregistrée pour le moment.</p>
          ) : (
            <div className="max-h-[420px] space-y-2 overflow-y-auto">
              {activity.map((e) => (
                <div key={e.id} className="flex items-center gap-3 rounded-xl border border-border p-3" data-testid={`activity-row-${e.id}`}>
                  {e.action === "add"
                    ? <PlusCircle className="h-5 w-5 shrink-0 text-emerald-500" />
                    : <MinusCircle className="h-5 w-5 shrink-0 text-destructive" />}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm">
                      <span className="font-medium">{e.user_email}</span>
                      <span className="text-muted-foreground"> ({e.user_role === "principal" ? "principal" : "secondaire"}) </span>
                      {e.action === "add" ? "a ajouté" : "a supprimé"}{" "}
                      {e.item_type === "poster" ? "l'affiche" : "l'image"} «&nbsp;{e.item_label}&nbsp;»
                      <span className="text-muted-foreground"> dans «&nbsp;{e.banner_name}&nbsp;»</span>
                    </p>
                    <p className="font-mono-label text-[10px] uppercase tracking-widest text-muted-foreground">
                      {new Date(e.timestamp).toLocaleString("fr-FR")}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
        </TabsContent>
      </Tabs>
      </main>
    </div>
  );
}
